// Every string here is persisted and read back by equality, so a rename is a
// data migration. ORIENTATION_PHASE_ORDER and FUNNEL_STAGES are walked by
// index, so their order is behavior. value-fidelity.test.ts pins both.

export const EnrollmentStatus = {
  Orientation: "orientation",
  Enrolled: "enrolled",
  Excluded: "excluded",
  Withdrawn: "withdrawn",
  Completed: "completed",
} as const;
export type EnrollmentStatus =
  (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

export const OrientationPhase = {
  Welcome: "welcome",
  HowItWorks: "howItWorks",
  Lnf: "lnf",
  Awrf: "awrf",
  Orf: "orf",
  Exploration: "exploration",
  ExploreCelebration: "exploreCelebration",
  MatchDemo: "matchDemo",
  Practice: "practice",
  PracticeCelebration: "practiceCelebration",
  ResultsReview: "resultsReview",
  DeviceSetup: "deviceSetup",
  Complete: "complete",
} as const;
export type OrientationPhase =
  (typeof OrientationPhase)[keyof typeof OrientationPhase];

// Not OrientationPhase's declaration order: the assessments run before
// HowItWorks. Keep the two distinct.
export const ORIENTATION_PHASE_ORDER: OrientationPhase[] = [
  OrientationPhase.Welcome,
  OrientationPhase.Lnf,
  OrientationPhase.Awrf,
  OrientationPhase.Orf,
  OrientationPhase.HowItWorks,
  OrientationPhase.Exploration,
  OrientationPhase.ExploreCelebration,
  OrientationPhase.MatchDemo,
  OrientationPhase.Practice,
  OrientationPhase.PracticeCelebration,
  OrientationPhase.ResultsReview,
  OrientationPhase.DeviceSetup,
  OrientationPhase.Complete,
];

// Shared by advancePhase, recordPhaseCompletion and the client's useNextPhase;
// keep gating logic (e.g. the LNF pass threshold) in the callers.
export function getNextActivePhase(
  currentPhase: string,
  skippedPhases?: readonly string[] | null,
): OrientationPhase | null {
  const currentIndex = ORIENTATION_PHASE_ORDER.indexOf(
    currentPhase as OrientationPhase,
  );
  if (currentIndex < 0) return null;
  const skipped = new Set(skippedPhases ?? []);
  // Complete is never skippable: it terminates the walk.
  return (
    ORIENTATION_PHASE_ORDER.slice(currentIndex + 1).find(
      (p) => p === OrientationPhase.Complete || !skipped.has(p),
    ) ?? OrientationPhase.Complete
  );
}

export const PHASE_DISPLAY_NAMES: Record<OrientationPhase, string> = {
  [OrientationPhase.Welcome]: "Welcome",
  [OrientationPhase.HowItWorks]: "How It Works",
  [OrientationPhase.Lnf]: "Letter Naming",
  [OrientationPhase.Awrf]: "Word Reading",
  [OrientationPhase.Orf]: "Passage Reading",
  [OrientationPhase.Exploration]: "Exploration",
  [OrientationPhase.ExploreCelebration]: "Exploration Celebration",
  [OrientationPhase.MatchDemo]: "Match Demo",
  [OrientationPhase.Practice]: "Practice",
  [OrientationPhase.PracticeCelebration]: "Practice Celebration",
  [OrientationPhase.ResultsReview]: "Results Review",
  [OrientationPhase.DeviceSetup]: "Device Setup",
  [OrientationPhase.Complete]: "Complete",
};

export const OrientationResponseType = {
  // Within 3 seconds.
  Recognized: "recognized",
  Guessed: "guessed",
  Skipped: "skipped",
} as const;
export type OrientationResponseType =
  (typeof OrientationResponseType)[keyof typeof OrientationResponseType];

export const ControlSource = {
  // Intake words are always control.
  Intake: "intake",
  Random: "random",
  // Pre-enrollment words have no controlSource at all.
} as const;
export type ControlSource = (typeof ControlSource)[keyof typeof ControlSource];

export const ExclusionReason = {
  // Both gates failed: LNF < 41 AND aWRF < 9.
  BelowInclusionThreshold: "below_inclusion_threshold",
  // Exactly isAboveCeiling in ./orientation, no score threshold. ../app
  // dropped this member; it stays here because the ceiling rule stands.
  AboveCeiling: "above_ceiling",
  ParticipantWithdrew: "participant_withdrew",
  Other: "other",
  // Included through LNF alone, and the parent chose not to enroll. Kept
  // apart from BelowInclusionThreshold: this child met the screen.
  ParentDeclinedLnfOnly: "parent_declined_lnf_only",
} as const;
export type ExclusionReason =
  (typeof ExclusionReason)[keyof typeof ExclusionReason];

export const EarlyExitReason = {
  LnfBelow16: "lnf_below_16",
  // Both gates failed: LNF < 41 AND aWRF < 9.
  BelowInclusion: "below_inclusion",
  // Same rule as ExclusionReason.AboveCeiling.
  AboveCeiling: "above_ceiling",
  Technical: "technical",
  ParentRequest: "parent_request",
} as const;
export type EarlyExitReason =
  (typeof EarlyExitReason)[keyof typeof EarlyExitReason];

export type EarlyExitOutcome =
  | {
      status: typeof EnrollmentStatus.Excluded;
      exclusionReason: ExclusionReason;
    }
  | { status: typeof EnrollmentStatus.Withdrawn }
  | { status: typeof EnrollmentStatus.Orientation };

// Technical exits stay in orientation so a rebooked call can finish them. An
// above_ceiling exit is only as good as the computed ceiling behind it: the
// server must not apply it unless phases.awrf.aboveCeiling is true.
export const EARLY_EXIT_OUTCOMES: Record<EarlyExitReason, EarlyExitOutcome> = {
  [EarlyExitReason.LnfBelow16]: {
    status: EnrollmentStatus.Excluded,
    exclusionReason: ExclusionReason.BelowInclusionThreshold,
  },
  [EarlyExitReason.BelowInclusion]: {
    status: EnrollmentStatus.Excluded,
    exclusionReason: ExclusionReason.BelowInclusionThreshold,
  },
  [EarlyExitReason.AboveCeiling]: {
    status: EnrollmentStatus.Excluded,
    exclusionReason: ExclusionReason.AboveCeiling,
  },
  [EarlyExitReason.Technical]: { status: EnrollmentStatus.Orientation },
  [EarlyExitReason.ParentRequest]: { status: EnrollmentStatus.Withdrawn },
};

export const RecordingConsent = {
  Pending: "pending",
  Given: "given",
  Declined: "declined",
} as const;
export type RecordingConsent =
  (typeof RecordingConsent)[keyof typeof RecordingConsent];

// An absent consent field is pending, so storage never holds "pending".
export const STORED_RECORDING_CONSENTS = [
  RecordingConsent.Given,
  RecordingConsent.Declined,
] as const;
export type StoredRecordingConsent = (typeof STORED_RECORDING_CONSENTS)[number];

export const TREATMENT_TARGETS = {
  A: { ratio: 0.5, count: 350 },
  B: { ratio: 0.65, count: 455 },
  C: { ratio: 0.8, count: 560 },
} as const;
export type StudyGroup = keyof typeof TREATMENT_TARGETS;

/** The group whose treatment ratio this is, or `null` for a ratio no group has. */
export function studyGroupOf(ratio: number): StudyGroup | null {
  for (const [group, target] of Object.entries(TREATMENT_TARGETS)) {
    if (target.ratio === ratio) return group as StudyGroup;
  }
  return null;
}

// τ_T/τ_C: committed words per arm that have ever reached Learned before the
// UnitBridging flip opens, sized so each group's treatment-control contrast
// is already powered without it. Protocol numbers.
export const LEARNED_WORD_THRESHOLDS = {
  A: { treatment: 100, control: 100 },
  B: { treatment: 130, control: 70 },
  C: { treatment: 160, control: 40 },
} as const satisfies Record<StudyGroup, { treatment: number; control: number }>;

export interface EverLearnedCounts {
  readonly everLearnedTreatment: number;
  readonly everLearnedControl: number;
}

/** Whether the reader has enough ever-Learned study words in both arms to open the flip. */
export function pastLearnedThreshold(
  counts: EverLearnedCounts,
  group: StudyGroup,
): boolean {
  const threshold = LEARNED_WORD_THRESHOLDS[group];
  return (
    counts.everLearnedTreatment >= threshold.treatment &&
    counts.everLearnedControl >= threshold.control
  );
}

// q: the share of bridge-eligible treatment draws flipped to unit_bridging.
// The analysis weights unflipped eligible treatment words by 1/(1 − q).
export const UNIT_BRIDGING_FLIP_PROBABILITY = 0.5;

// Per word and per reader for the whole study, committed when the word first
// enters developing. "provisional" is the arm the child's device already
// applied; "learning_record" is the arm the tap's learning record shows the
// child saw when the device had none to send; "server_draw" is a draw made
// because the response arrived with neither. "unit_bridging" is never drawn:
// it is a treatment draw flipped once the reader is past the learned-word
// threshold, and still counts as treatment in the study's ratio.
export const ASSIGNMENT_ARMS = [
  "treatment",
  "control",
  "unit_bridging",
] as const;
export type AssignmentArm = (typeof ASSIGNMENT_ARMS)[number];

export const ASSIGNMENT_COMMIT_SOURCES = [
  "provisional",
  "server_draw",
  "learning_record",
] as const;
export type AssignmentCommitSource = (typeof ASSIGNMENT_COMMIT_SOURCES)[number];

export const TARGET_VOCABULARY_SIZE = 700;

export const LNF_INCLUSION_MINIMUM = 41;

export const AWRF_INCLUSION_MINIMUM = 9;

// The parent's answer when the screen is met through LNF alone: that child
// will need extra support, so enrolling is the parent's choice, never
// automatic.
export const PARENT_ENROLLMENT_CHOICES = [
  "enroll_with_support",
  "decline",
] as const;
export type ParentEnrollmentChoice = (typeof PARENT_ENROLLMENT_CHOICES)[number];

// participant_event.reason on the enrolled event of a parent-chosen
// enrollment; an automatic enrollment carries none.
export const PARENT_CHOSE_LNF_ONLY = "parent_chose_lnf_only";

export interface InclusionScores {
  readonly lnfScore: number;
  readonly awrfRawScore: number;
  readonly aboveCeiling: boolean;
}

/** Included by LNF ≥ 41 while aWRF is below 9 (a discontinue scores 0),
 *  and not excluded above the ceiling: the enrollment waits on the parent. */
export function inclusionRestsOnLnfAlone({
  lnfScore,
  awrfRawScore,
  aboveCeiling,
}: InclusionScores): boolean {
  return (
    !aboveCeiling &&
    lnfScore >= LNF_INCLUSION_MINIMUM &&
    awrfRawScore < AWRF_INCLUSION_MINIMUM
  );
}

export const LNF_PASS_THRESHOLD = 16;

export const PRACTICE_MATCH_TARGET = 10;

// LNF is a hard 60 s stop, so the form only needs to outlast the minute.
export const LNF_FORM_LENGTH = 100;

// Pinned so loading another year into dibels_8_wrf can never interleave two
// years' lists under ORDER BY sequence.
export const WRF_LIST_YEAR = 2020;

export const WRF_NORMS_YEAR = 2022;

// Applies only above grade K: there is no lower list to drop to.
export const AWRF_DOWNGRADE_PERCENTILE = 10;

export const LNF_MAX_GRADE = 1;

export const RESULTS_REVIEW_TOTAL_STEPS = 7;

export const BookmarkStatus = {
  Generated: "generated",
  Distributed: "distributed",
  Activated: "activated",
  Revoked: "revoked",
  Completed: "completed",
} as const;
export type BookmarkStatus =
  (typeof BookmarkStatus)[keyof typeof BookmarkStatus];

// The printed code is the lookup key in study_bookmarks, so the shape is
// persisted: changing any of these strands every bookmark already printed.
export const BOOKMARK_CODE_PREFIX = "SARA-";
export const BOOKMARK_CODE_LENGTH = 6;
export const BOOKMARK_CODE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
export const BOOKMARK_CODE_PATTERN = /^SARA-[A-Z0-9]{6}$/;

/** Accepts what a person types off the card: any case, stray spaces anywhere. */
export function normalizeBookmarkCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/\s+/g, "");
}

export function isBookmarkCode(raw: string): boolean {
  return BOOKMARK_CODE_PATTERN.test(normalizeBookmarkCode(raw));
}

export const VENUE_CHANNELS = [
  "bookstore",
  "lesson_center",
  "head_start",
  "wic_clinic",
  "boys_girls_club",
  "rec_center",
  "community_org",
  "library",
  "other",
] as const;

export type VenueChannel = (typeof VENUE_CHANNELS)[number];

export const VENUE_CHANNEL_LABELS: Record<VenueChannel, string> = {
  bookstore: "Bookstore",
  lesson_center: "Lesson center",
  head_start: "Head Start",
  wic_clinic: "WIC clinic",
  boys_girls_club: "Boys & Girls Club",
  rec_center: "Rec center",
  community_org: "Community org",
  library: "Library",
  other: "Other",
};

export const FUNNEL_STAGES = [
  "landed",
  "scanned",
  "signed_up",
  "consented",
  "scheduled",
  "confirmed",
  "active",
] as const;

// "abandoned" is in the type but deliberately not the array: that keeps it
// unreachable by ordered advancement and ranks it -1. Do not add it.
export type FunnelStage = (typeof FUNNEL_STAGES)[number] | "abandoned";

export function funnelStageRank(stage: FunnelStage): number {
  return FUNNEL_STAGES.indexOf(stage as (typeof FUNNEL_STAGES)[number]);
}

export const ENROLLED_STAGES: readonly FunnelStage[] = [
  "scheduled",
  "confirmed",
  "active",
];
